import { PitchDetector } from "pitchy";
import { Note } from "../../music/note";
import { Piano } from "../piano";
import { PianoInput } from "./input";

/** Convert a frequency (Hz) to the nearest MIDI note number. */
function frequencyToMidi(freq: number): number {
  return Math.round(12 * Math.log2(freq / 440) + 69);
}

/** Compute the RMS amplitude of a Float32Array signal (0..1 range). */
function rms(buffer: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < buffer.length; i++) {
    sum += buffer[i] * buffer[i];
  }

  return Math.sqrt(sum / buffer.length);
}

export interface MicrophoneInputOptions {
  /**
   * Minimum pitch clarity (0..1) to accept a detected note.
   * Higher = fewer false positives but may miss quieter notes.
   * @default 0.80
   */
  clarityThreshold?: number;

  /**
   * Minimum RMS amplitude to consider the signal non-silent.
   * Below this the mic is treated as "no input".
   * @default 0.005
   */
  silenceThreshold?: number;

  /**
   * Number of consecutive detection frames a new note must be stable
   * before triggering a press (prevents jitter between adjacent semitones).
   * @default 3
   */
  stabilityFrames?: number;

  /** AnalyserNode FFT size. Must be a power of 2. @default 4096 */
  fftSize?: number;

  /** Enable console debug logging of detection values. @default false */
  debug?: boolean;
}

/**
 * Microphone-based pitch detection input source.
 *
 * Uses the Web Audio API to capture the microphone stream and the `pitchy`
 * library (McLeod Pitch Method) to detect the fundamental frequency in
 * real-time. Detected notes are forwarded to the Piano instance exactly
 * like any other PianoInput.
 *
 * Limitations:
 * - Monophonic only (one note at a time)
 * - ~50-100 ms latency due to the analysis buffer
 * - No sustain pedal detection
 */
export class MicrophoneInput implements PianoInput {
  private stream: MediaStream | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private detector: PitchDetector<Float32Array> | null = null;
  private rafId: number | null = null;
  private inputBuffer: Float32Array | null = null;

  /** Currently held MIDI note (null = silence). */
  private currentMidi: number | null = null;

  /** Candidate note being validated for stability. */
  private candidateMidi: number | null = null;
  /** How many consecutive frames the candidate has been stable. */
  private candidateCount = 0;

  // ── Options ──
  /**
   * All three detection parameters below are mutable at runtime so the
   * studio settings overlay can drive the sensitivity slider, see
   * {@link setDetectionParams}. The slider maps a single 0–100 value to
   * a coordinated tuple of (silence, clarity, stability), touching only
   * silence is not enough to feel responsive across the range.
   */
  private silenceThreshold: number;
  private clarityThreshold: number;
  private stabilityFrames: number;
  private readonly fftSize: number;
  private readonly debug: boolean;

  /** Throttle debug logs to avoid flooding the console. */
  private debugCounter = 0;

  constructor(
    private readonly piano: Piano,
    private readonly ctx: AudioContext,
    opts?: MicrophoneInputOptions,
  ) {
    this.clarityThreshold = opts?.clarityThreshold ?? 0.80;
    this.silenceThreshold = opts?.silenceThreshold ?? 0.005;
    this.stabilityFrames = opts?.stabilityFrames ?? 3;
    this.fftSize = opts?.fftSize ?? 4096;
    this.debug = opts?.debug ?? false;
  }

  // ── PianoInput interface ──

  /**
   * Start the detection loop.
   *
   * **Important:** On iOS/iPadOS Safari, `getUserMedia()` must be called
   * directly in the synchronous call-stack of the user gesture (tap/click).
   * The caller is therefore responsible for obtaining the stream **before**
   * constructing / attaching this input.  Pass the stream via the constructor
   * or {@link setStream} before calling `attach()`.
   */
  setStream(stream: MediaStream): void {
    this.stream = stream;
  }

  attach(): void {
    if (!this.stream) {
      throw new Error(
        "MicrophoneInput: call setStream(stream) before attach(). " +
        "getUserMedia() must be called directly in the user-gesture handler.",
      );
    }

    // Resume AudioContext if suspended (iOS creates it suspended).
    // `!== "running"` couvre aussi `interrupted`, que Safari pose au retour
    // d'arrière-plan et qui ne figure pas dans le type du DOM.
    if (this.ctx.state !== "running") {
      void this.ctx.resume().catch(() => undefined);
    }

    this.sourceNode = this.ctx.createMediaStreamSource(this.stream);

    this.analyserNode = this.ctx.createAnalyser();
    this.analyserNode.fftSize = this.fftSize;

    this.sourceNode.connect(this.analyserNode);
    // Do NOT connect analyser to ctx.destination, we don't want mic passthrough.

    this.inputBuffer = new Float32Array(this.analyserNode.fftSize);
    this.detector = PitchDetector.forFloat32Array(this.analyserNode.fftSize);

    this.loop();
  }

  /**
   * Live-analyser accessor, used by the settings overlay VU meter.
   * Returns `null` until `attach()` has been called and a stream is active.
   */
  getAnalyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  /**
   * Live-tunable detection parameters. Any subset can be passed; only the
   * provided fields are updated. Used by the studio settings overlay to
   * drive the sensitivity slider with a single coordinated curve.
   *
   * Lower `silenceThreshold` and `clarityThreshold` and lower
   * `stabilityFrames` all tend to catch quieter / faster / less stable
   * notes, at the cost of more false positives.
   */
  setDetectionParams(params: {
    silenceThreshold?: number;
    clarityThreshold?: number;
    stabilityFrames?: number;
  }): void {
    if (params.silenceThreshold !== undefined) {
      this.silenceThreshold = params.silenceThreshold;
    }

    if (params.clarityThreshold !== undefined) {
      this.clarityThreshold = params.clarityThreshold;
    }

    if (params.stabilityFrames !== undefined) {
      this.stabilityFrames = Math.max(1, Math.round(params.stabilityFrames));
    }
  }

  detach(): void {
    // Stop detection loop
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    // Release current note
    this.releaseCurrentNote();

    // Disconnect audio graph
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    this.analyserNode = null;
    this.detector = null;
    this.inputBuffer = null;

    // Stop mic stream
    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        track.stop();
      }

      this.stream = null;
    }

    // Reset stability state
    this.candidateMidi = null;
    this.candidateCount = 0;
  }

  // ── Detection loop ──

  private loop = (): void => {
    this.detect();
    this.rafId = requestAnimationFrame(this.loop);
  };

  private detect(): void {
    if (!this.analyserNode || !this.detector || !this.inputBuffer) {
      return;
    }

    this.analyserNode.getFloatTimeDomainData(
      this.inputBuffer as Float32Array<ArrayBuffer>,
    );

    // ── Silence gate ──
    const amplitude = rms(this.inputBuffer);

    if (this.debug && ++this.debugCounter % 30 === 0) {
      // Log every ~0.5s at 60fps
      const [f, c] = this.detector.findPitch(this.inputBuffer, this.ctx.sampleRate);
      const midi = f > 0 ? frequencyToMidi(f) : -1;
      console.log(
        `[mic] amp=${amplitude.toFixed(4)} freq=${f.toFixed(1)}Hz clarity=${c.toFixed(3)} midi=${midi} (threshold: clarity>${this.clarityThreshold} amp>${this.silenceThreshold})`,
      );
    }

    if (amplitude < this.silenceThreshold) {
      this.releaseCurrentNote();
      this.candidateMidi = null;
      this.candidateCount = 0;

      return;
    }

    // ── Pitch detection ──
    const [frequency, clarity] = this.detector.findPitch(
      this.inputBuffer,
      this.ctx.sampleRate,
    );

    if (clarity < this.clarityThreshold) {
      // Signal present but pitch unclear, keep holding current note
      // (avoids releasing during transients / attack portions)
      this.candidateMidi = null;
      this.candidateCount = 0;

      return;
    }

    const midi = frequencyToMidi(frequency);

    // Clamp to valid MIDI range
    if (midi < 0 || midi > 127) {
      return;
    }

    // If the detected note is already the current note, nothing to do
    if (midi === this.currentMidi) {
      this.candidateMidi = null;
      this.candidateCount = 0;

      return;
    }

    // ── Stability check ──
    if (midi === this.candidateMidi) {
      this.candidateCount++;
    } else {
      this.candidateMidi = midi;
      this.candidateCount = 1;
    }

    if (this.candidateCount >= this.stabilityFrames) {
      // Stable new note, switch
      this.releaseCurrentNote();

      try {
        const note = Note.fromMidi(midi);
        if (this.piano.contains(note)) {
          // Estimate velocity from amplitude (map RMS 0.005..0.3 → 0.3..1.0)
          const velocity = Math.min(
            1.0,
            0.3 + ((amplitude - 0.005) / 0.295) * 0.7,
          );
          this.piano.press(note, velocity);
          this.currentMidi = midi;

          if (this.debug) {
            console.log(`[mic] NOTE ON: ${note.toName()} (midi=${midi}, vel=${velocity.toFixed(2)})`);
          }
        }
      } catch {
        // Note out of piano range, ignore
      }

      this.candidateMidi = null;
      this.candidateCount = 0;
    }
  }

  private releaseCurrentNote(): void {
    if (this.currentMidi != null) {
      try {
        this.piano.release(Note.fromMidi(this.currentMidi));
      } catch {
        // Ignore if note is invalid
      }

      this.currentMidi = null;
    }
  }
}
